import { Provider } from 'store/api/enums';
import type { TrackingCategory } from 'store/organisation/models';
import {
  DRAWER_TRACKING_FIELD_ORDER,
  getEditDrawerLabel,
  TRACKING_CATEGORY_1_COLUMN_KEY,
  TRACKING_CATEGORY_2_COLUMN_KEY,
} from './constants';

export type TrackingOptionFieldKey =
  (typeof DRAWER_TRACKING_FIELD_ORDER)[number];

export type TrackingOptionSelectOption = {
  label: string;
  value: string;
  name?: string;
};

export function entitySupportsTrackingCategoryFields(
  providerName: string | undefined | null
): boolean {
  const normalized = providerName?.toLowerCase();
  return (
    Boolean(normalized) &&
    normalized !== Provider.EXCEL.toLowerCase() &&
    normalized !== Provider.RECEIPTS.toLowerCase()
  );
}

/** True when tracking categories are already on the organisation in store (no fetch needed). */
export function hasTrackingCategoriesLoaded(
  organisation:
    | {
        trackingCategories?: TrackingCategory[];
      }
    | null
    | undefined
): boolean {
  return organisation?.trackingCategories !== undefined;
}

/** Show tracking fields in extraction rules only when data is already available. */
export function shouldIncludeTrackingCategoryFields(
  organisation:
    | {
        providerName?: string | null;
        trackingCategories?: TrackingCategory[];
      }
    | null
    | undefined
): boolean {
  return (
    entitySupportsTrackingCategoryFields(organisation?.providerName) &&
    hasTrackingCategoriesLoaded(organisation)
  );
}

function trackingFieldFallbackLabel(fieldKey: TrackingOptionFieldKey): string {
  return getEditDrawerLabel(fieldKey);
}

export function buildTrackingFieldLabels(
  trackingCategories: TrackingCategory[] | undefined | null
): Record<TrackingOptionFieldKey, string> {
  return {
    trackingOption1Id:
      trackingCategories?.[0]?.name?.trim() ||
      getEditDrawerLabel(TRACKING_CATEGORY_1_COLUMN_KEY) ||
      trackingFieldFallbackLabel('trackingOption1Id'),
    trackingOption2Id:
      trackingCategories?.[1]?.name?.trim() ||
      getEditDrawerLabel(TRACKING_CATEGORY_2_COLUMN_KEY) ||
      trackingFieldFallbackLabel('trackingOption2Id'),
  };
}

export function trackingOptionIdsEqual(a: unknown, b: unknown): boolean {
  const left = String(a ?? '').trim();
  const right = String(b ?? '').trim();
  return Boolean(left) && left === right;
}

export function findTrackingOptionLabel(
  optionId: string,
  trackingCategories: TrackingCategory[] | undefined | null,
  dimensionIndex: number
): string {
  const id = String(optionId ?? '').trim();
  if (!id || !trackingCategories?.length) return '';

  const lookupIn = (index: number): string => {
    const match = (trackingCategories[index]?.options ?? []).find((option) =>
      trackingOptionIdsEqual(option.id, id)
    );
    return match?.name?.trim() ?? '';
  };

  const preferred = lookupIn(dimensionIndex);
  if (preferred) return preferred;

  for (let i = 0; i < trackingCategories.length; i++) {
    if (i === dimensionIndex) continue;
    const fromOther = lookupIn(i);
    if (fromOther) return fromOther;
  }
  return '';
}

/**
 * Prefer the name stored on the transaction, then look up by option id
 * (preferred dimension, then any other). Returns '' when only an unmatched
 * id is available so the UI does not flash raw ids.
 */
export function resolveTrackingOptionDisplayLabel(
  optionId: string | null | undefined,
  optionName: string | null | undefined,
  trackingCategories: TrackingCategory[] | undefined | null,
  dimensionIndex: number
): string {
  const name = optionName != null ? String(optionName).trim() : '';
  if (name) return name;
  const id = optionId != null ? String(optionId).trim() : '';
  if (!id) return '';
  return findTrackingOptionLabel(id, trackingCategories, dimensionIndex);
}

export function buildTrackingOptionSelectOptions(
  trackingCategories: TrackingCategory[] | undefined | null,
  dimensionIndex: 0 | 1
): TrackingOptionSelectOption[] {
  const category = trackingCategories?.[dimensionIndex];
  const options: TrackingOptionSelectOption[] = [];
  for (const option of category?.options ?? []) {
    const name = String(option?.name ?? '').trim();
    const value = String(option?.id ?? '').trim();
    if (!name || !value) continue;
    options.push({ label: name, value, name });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

/** Options for a Select, plus a name-only fallback when the current id is not in the list. */
export function trackingSelectOptionsForValue(
  trackingCategories: TrackingCategory[] | undefined | null,
  dimensionIndex: 0 | 1,
  currentId: string,
  optionName?: string | null
): TrackingOptionSelectOption[] {
  const options = buildTrackingOptionSelectOptions(
    trackingCategories,
    dimensionIndex
  );
  const id = String(currentId ?? '').trim();
  if (!id || options.some((option) => option.value === id)) return options;
  const label = resolveTrackingOptionDisplayLabel(
    id,
    optionName,
    trackingCategories,
    dimensionIndex
  );
  if (!label) return options;
  return [{ label, value: id, name: label }, ...options];
}

export function buildTrackingOptionFieldOptions(
  trackingCategories: TrackingCategory[] | undefined | null
): {
  trackingOption1Options: TrackingOptionSelectOption[];
  trackingOption2Options: TrackingOptionSelectOption[];
  trackingFieldLabels: Record<TrackingOptionFieldKey, string>;
} {
  return {
    trackingOption1Options: buildTrackingOptionSelectOptions(
      trackingCategories,
      0
    ),
    trackingOption2Options: buildTrackingOptionSelectOptions(
      trackingCategories,
      1
    ),
    trackingFieldLabels: buildTrackingFieldLabels(trackingCategories),
  };
}
